/**
 * ShareInstance - Single Share Bar Instance
 * Represents a single share bar that can be mounted to a target element.
 * Managed by ShareKit for coordinated updates.
 */

import type {
  SocialShareConfig,
  SocialShareState,
  ShareContent,
  PositionMode,
  PlatformId,
  Platform,
  EventCallback,
  SocialShareEvent,
  LayoutMode,
  BlockShareConfig,
} from '../types';
import { mergeConfig, DEFAULT_CONFIG } from './config';
import { getPlatforms } from '../platforms';
import { generateCSS, injectStyles, removeStyles, StyleConfig } from '../ui/styles';
import {
  createShareBar,
  updateShareBarPosition,
  updateShareLinks,
  showTooltip,
  hideTooltip,
  checkStaticOverflow,
} from '../ui/ShareBar';
import { copyToClipboard } from '../utils/clipboard';

export interface ShareInstanceConfig extends SocialShareConfig {
  /** If true, skip auto-mounting (used by ShareKit factory methods) */
  skipAutoMount?: boolean;
}

export class ShareInstance {
  private config: ReturnType<typeof mergeConfig>;
  private state: SocialShareState;
  private platforms: Platform[];
  private eventListeners: Map<SocialShareEvent, Set<EventCallback>>;
  private skipAutoMount: boolean;

  constructor(userConfig: ShareInstanceConfig) {
    const { skipAutoMount, ...socialShareConfig } = userConfig;
    this.skipAutoMount = skipAutoMount ?? false;
    this.config = mergeConfig(socialShareConfig);
    this.platforms = getPlatforms(this.config.platforms);
    this.eventListeners = new Map();
    
    this.state = {
      mounted: false,
      visible: false,
      container: null,
      rootElement: null,
      styleElement: null,
      currentPosition: this.resolvePosition(this.config.position),
      resizeObserver: null,
    };
    
    // Auto-mount and display (unless skipAutoMount is true)
    if (!this.skipAutoMount) {
      this.autoMount();
    }
  }
  
  /**
   * Auto-mount based on position and target config
   * - 'static' position: mounts to target element
   * - other positions: mounts to document.body
   */
  private autoMount(): void {
    // Wait for DOM to be ready
    if (typeof document === 'undefined') return;
    
    const doMount = () => {
      if (this.config.position === 'static') {
        // Static position requires a target
        if (!this.config.target) {
          console.error('ShareInstance: Static position requires a target selector.');
          return;
        }
        const targetElement = document.querySelector<HTMLElement>(this.config.target);
        if (!targetElement) {
          console.error('ShareInstance: Target element not found:', this.config.target);
          return;
        }
        this.mount(targetElement);
      } else {
        // Auto/left/bottom positions mount to body
        this.mount(document.body);
      }
      this.show();
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', doMount);
    } else {
      doMount();
    }
  }

  // ============================================
  // Public API - Getters and Setters
  // ============================================

  /** Get/set position mode */
  get position(): PositionMode {
    return this.config.position;
  }

  set position(value: PositionMode) {
    this.config.position = value;
    this.state.currentPosition = this.resolvePosition(value);
    if (this.state.rootElement) {
      updateShareBarPosition(this.state.rootElement, this.state.currentPosition);
    }
  }

  /** Get/set auto width threshold */
  get autoWidthThreshold(): number {
    return this.config.autoWidthThreshold;
  }

  set autoWidthThreshold(value: number) {
    this.config.autoWidthThreshold = value;
    this.refreshStyles();
  }

  /** Get/set content URL */
  get contentUrl(): string {
    return this.config.content.url || '';
  }

  set contentUrl(value: string) {
    this.config.content.url = value;
    this.refreshLinks();
  }

  /** Get/set content title */
  get contentTitle(): string {
    return this.config.content.title || '';
  }

  set contentTitle(value: string) {
    this.config.content.title = value;
    this.refreshLinks();
  }

  /** Get/set content description */
  get contentDescription(): string {
    return this.config.content.description || '';
  }

  set contentDescription(value: string) {
    this.config.content.description = value;
    this.refreshLinks();
  }

  /** Get/set content image */
  get contentImage(): string {
    return this.config.content.image || '';
  }

  set contentImage(value: string) {
    this.config.content.image = value;
    this.refreshLinks();
  }

  /** Get/set content hashtags */
  get contentHashtags(): string[] {
    return this.config.content.hashtags || [];
  }

  set contentHashtags(value: string[]) {
    this.config.content.hashtags = value;
    this.refreshLinks();
  }

  /** Get/set icon sprite path */
  get iconSpritePath(): string {
    return this.config.iconSpritePath;
  }

  set iconSpritePath(value: string) {
    this.config.iconSpritePath = value;
    this.refreshStyles();
  }

  /** Get/set z-index */
  get zIndex(): number {
    return this.config.zIndex;
  }

  set zIndex(value: number) {
    this.config.zIndex = value;
    this.refreshStyles();
  }

  /** Get current content */
  get content(): ShareContent {
    return { ...this.config.content };
  }

  // ============================================
  // Public API - Methods
  // ============================================

  /**
   * Mount the share bar to a container element
   * @param target - CSS selector or HTMLElement
   */
  mount(target: string | HTMLElement): this {
    if (this.state.mounted) {
      console.warn('ShareInstance: Already mounted. Call unmount() first.');
      return this;
    }

    const container = typeof target === 'string'
      ? document.querySelector<HTMLElement>(target)
      : target;

    if (!container) {
      console.error('ShareInstance: Mount target not found:', target);
      return this;
    }

    this.state.container = container;
    this.state.mounted = true;

    // Inject styles
    this.injectStyles();

    // Create share bar
    this.state.rootElement = createShareBar(
      {
        platforms: this.platforms,
        labels: this.config.labels as Record<string, string>,
        onButtonClick: this.handleButtonClick.bind(this),
        mode: this.config.mode,
        shape: this.config.shape,
        blockConfig: this.config.blockConfig,
      },
      this.config.content,
      this.state.currentPosition
    );

    // Mount to container
    if (this.config.position === 'static') {
      // For block mode, ensure the container fills available horizontal space
      if (this.config.mode === 'block') {
        container.style.flex = '1 1 0%';
        container.style.minWidth = '0';
      }
      container.appendChild(this.state.rootElement);
      // Check overflow after mounting
      requestAnimationFrame(() => {
        if (this.state.rootElement) {
          checkStaticOverflow(
            this.state.rootElement,
            this.platforms,
            this.config.iconSize,
            this.config.buttonMargin
          );
        }
      });
    } else {
      document.body.appendChild(this.state.rootElement);
    }

    // Setup resize observer for auto position
    if (this.config.position === 'auto') {
      this.setupResizeObserver();
    }

    this.emit('mount', { container });
    return this;
  }

  /**
   * Unmount the share bar
   */
  unmount(): this {
    if (!this.state.mounted) return this;

    // Remove resize observer
    if (this.state.resizeObserver) {
      this.state.resizeObserver.disconnect();
      this.state.resizeObserver = null;
    }

    // Remove elements
    if (this.state.rootElement) {
      this.state.rootElement.remove();
      this.state.rootElement = null;
    }

    if (this.state.styleElement) {
      removeStyles(this.state.styleElement);
      this.state.styleElement = null;
    }

    this.state.mounted = false;
    this.state.visible = false;
    this.state.container = null;

    this.emit('unmount', {});
    return this;
  }

  /**
   * Show the share bar
   */
  show(): this {
    if (!this.state.mounted || !this.state.rootElement) {
      console.warn('ShareInstance: Not mounted. Call mount() first.');
      return this;
    }

    this.state.rootElement.style.display = 'flex';
    this.state.visible = true;
    this.emit('show', {});
    return this;
  }

  /**
   * Hide the share bar
   */
  hide(): this {
    if (!this.state.rootElement) return this;

    this.state.rootElement.style.display = 'none';
    this.state.visible = false;
    this.emit('hide', {});
    return this;
  }

  /**
   * Display the share bar (alias for show)
   */
  display(): this {
    return this.show();
  }

  /**
   * Update content to share
   */
  setContent(content: Partial<ShareContent>): this {
    this.config.content = {
      ...this.config.content,
      ...content,
    };
    this.refreshLinks();
    return this;
  }

  /**
   * Update the instance with new content (called by ShareKit.updateContent)
   */
  update(content: Partial<ShareContent>): this {
    return this.setContent(content);
  }

  /**
   * Update platform labels
   */
  setLabels(labels: Partial<Record<PlatformId, string>>): this {
    this.config.labels = {
      ...this.config.labels,
      ...labels,
    };
    // Labels are set at creation time, need to rebuild
    if (this.state.mounted) {
      this.rebuild();
    }
    return this;
  }

  /**
   * Add event listener
   */
  on<T = unknown>(event: SocialShareEvent, callback: EventCallback<T>): this {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback as EventCallback);
    return this;
  }

  /**
   * Remove event listener
   */
  off<T = unknown>(event: SocialShareEvent, callback: EventCallback<T>): this {
    this.eventListeners.get(event)?.delete(callback as EventCallback);
    return this;
  }

  /**
   * Destroy the instance completely
   */
  destroy(): void {
    this.unmount();
    this.eventListeners.clear();
  }

  /**
   * Check if mounted
   */
  isMounted(): boolean {
    return this.state.mounted;
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.state.visible;
  }

  // ============================================
  // Private Methods
  // ============================================

  private handleButtonClick(platform: Platform, event: MouseEvent): void {
    // For copy link, handle differently
    if (platform.id === 'copylink') {
      event.preventDefault();
      this.handleCopyLink();
      return;
    }

    // Emit share event
    this.emit('share', {
      platform: platform.id,
      content: this.config.content,
    });

    // Call onShare callback if provided
    this.config.onShare?.(platform.id, this.config.content);
  }

  private async handleCopyLink(): Promise<void> {
    const url = this.config.content.url || window.location.href;
    const success = await copyToClipboard(url);

    if (success && this.state.rootElement) {
      // Show tooltip
      showTooltip('copylink', this.state.rootElement);

      // Hide after delay
      setTimeout(() => {
        if (this.state.rootElement) {
          hideTooltip('copylink', this.state.rootElement);
        }
      }, 2000);
    }

    // Emit copy event
    this.emit('copy', { url, success });

    // Call onCopy callback if provided
    this.config.onCopy?.(url);
  }

  private injectStyles(): void {
    const styleConfig: StyleConfig = {
      iconSpritePath: this.config.iconSpritePath,
      iconSize: this.config.iconSize,
      buttonOpacity: this.config.buttonOpacity,
      buttonMargin: this.config.buttonMargin,
      buttonPadding: this.config.buttonPadding,
      zIndex: this.config.zIndex,
      autoWidthThreshold: this.config.autoWidthThreshold,
      mode: this.config.mode,
      shape: this.config.shape,
      blockConfig: this.config.blockConfig,
    };

    // Generate unique ID based on style configuration to prevent duplicate style tags
    // IMPORTANT: Include mode AND shape to avoid style caching collisions
    const blockPart = this.config.mode === 'block' 
      ? `-block-${this.config.blockConfig?.height || 40}-${this.config.blockConfig?.gap || 1}-${this.config.blockConfig?.radius || 4}` 
      : '';
    const styleId = `sharekit-style-${this.config.mode}-${this.config.shape}-${styleConfig.iconSize}-${styleConfig.buttonMargin}-${styleConfig.buttonPadding}-${styleConfig.buttonOpacity}-${styleConfig.zIndex}-${styleConfig.autoWidthThreshold}${blockPart}`;

    const css = generateCSS(this.platforms, styleConfig);
    this.state.styleElement = injectStyles(css, styleId);
  }

  private refreshStyles(): void {
    if (!this.state.mounted) return;

    // Remove old styles
    if (this.state.styleElement) {
      removeStyles(this.state.styleElement);
    }

    // Inject new styles
    this.injectStyles();
  }

  private refreshLinks(): void {
    if (!this.state.rootElement) return;
    updateShareLinks(this.state.rootElement, this.platforms, this.config.content);
  }

  private rebuild(): void {
    const wasVisible = this.state.visible;
    const container = this.state.container;

    this.unmount();

    if (container) {
      this.mount(container);
      if (wasVisible) {
        this.show();
      }
    }
  }

  private resolvePosition(position: PositionMode): 'auto' | 'left' | 'bottom' | 'static' {
    // Return the position as-is - let CSS handle 'auto' via media queries
    return position;
  }

  private setupResizeObserver(): void {
    // Use matchMedia for more efficient width checking
    const mediaQuery = window.matchMedia(`(min-width: ${this.config.autoWidthThreshold}px)`);

    const handleChange = () => {
      this.state.currentPosition = this.resolvePosition('auto');
      // Position is handled by CSS media queries when using 'auto'
    };

    mediaQuery.addEventListener('change', handleChange);

    // Store for cleanup (using ResizeObserver slot for simplicity)
    this.state.resizeObserver = {
      disconnect: () => mediaQuery.removeEventListener('change', handleChange),
    } as unknown as ResizeObserver;
  }

  private emit(event: SocialShareEvent, data: unknown): void {
    this.eventListeners.get(event)?.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`ShareInstance: Error in ${event} event handler:`, error);
      }
    });
  }
}

// Legacy alias for backward compatibility
export { ShareInstance as SocialShare };

